import React, { useState, useEffect } from 'react';
import { translationsApi } from '../../../lib/api/translations';

interface FooterContent {
  companyDescription: string;
  vatInfo: string;
  kmkrNumber: string;
  businessHours: string;
  address: string;
  phone1: string;
  phone2: string;
  email: string;
  copyright: string;
}

const FooterEditor: React.FC = () => {
  const [footerContent, setFooterContent] = useState<FooterContent>({
    companyDescription: '',
    vatInfo: '',
    kmkrNumber: '',
    businessHours: '',
    address: '',
    phone1: '',
    phone2: '',
    email: '',
    copyright: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default values from the website
  const defaultContent = {
    companyDescription: 'Eesti metsandussektori juhtiv partner juba 20 aastat. Pakume kvaliteetseid teenuseid metsamaa ja metsamaterjali ostust-müügist kuni transportini üle kogu Eesti.',
    vatInfo: 'Käibemaksukohustuslane',
    kmkrNumber: 'KMKR nr EE123456789',
    businessHours: 'Lahtiolekuajad: E-R 8:00-17:00',
    address: 'Osmussaare tänav 8, 13811 Tallinn, Eesti',
    phone1: '+372 6707 110',
    phone2: '+372 5663 6076',
    email: 'info@forestryest.ee',
    copyright: '© 2025 Forestry OÜ. Kõik õigused kaitstud. Reg.nr 12345678'
  };

  useEffect(() => {
    loadFooterContent();
  }, []);

  const loadFooterContent = async () => {
    setLoading(true);
    try {
      const keys = [
        'footer.companyDescription',
        'footer.vatInfo',
        'footer.kmkrNumber',
        'footer.businessHours',
        'footer.contact.address',
        'footer.contact.phone1',
        'footer.contact.phone2',
        'footer.contact.email',
        'footer.copyright'
      ];

      const translations: Record<string, string> = {};
      
      for (const key of keys) {
        const { data } = await translationsApi.getTranslation(key);
        const shortKey = key.split('.').pop() || key;
        translations[shortKey] = data?.value || defaultContent[shortKey as keyof typeof defaultContent] || '';
      }

      setFooterContent({
        companyDescription: translations.companyDescription,
        vatInfo: translations.vatInfo,
        kmkrNumber: translations.kmkrNumber,
        businessHours: translations.businessHours,
        address: translations.address,
        phone1: translations.phone1,
        phone2: translations.phone2,
        email: translations.email,
        copyright: translations.copyright
      });
    } catch (err) {
      console.error('Failed to load footer content:', err);
      setFooterContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  const handleContentSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const translationUpdates = [
        { key: 'footer.companyDescription', value: footerContent.companyDescription, locale: 'et' },
        { key: 'footer.vatInfo', value: footerContent.vatInfo, locale: 'et' },
        { key: 'footer.kmkrNumber', value: footerContent.kmkrNumber, locale: 'et' },
        { key: 'footer.businessHours', value: footerContent.businessHours, locale: 'et' },
        { key: 'footer.contact.address', value: footerContent.address, locale: 'et' },
        { key: 'footer.contact.phone1', value: footerContent.phone1, locale: 'et' },
        { key: 'footer.contact.phone2', value: footerContent.phone2, locale: 'et' },
        { key: 'footer.contact.email', value: footerContent.email, locale: 'et' },
        { key: 'footer.copyright', value: footerContent.copyright, locale: 'et' }
      ];

      const { error } = await translationsApi.upsertTranslations(translationUpdates);

      if (error) {
        setError('Jaluse sisu salvestamine ebaõnnestus');
        return;
      }

      // Trigger website update
      window.dispatchEvent(new CustomEvent('translationsUpdated'));
      
      // Show success message briefly
      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
      successDiv.textContent = 'Jaluse sisu edukalt salvestatud!';
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);

    } catch (err) {
      setError('Jaluse sisu salvestamine ebaõnnestus');
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = (field: keyof FooterContent, value: string) => {
    setFooterContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Jaluse haldus</h1>
        <p className="mt-2 text-gray-600">
          Halda kõiki jaluse sektsioonis kuvatavaid tekste ja kontaktandmeid. Muudatused kajastuvad kohe veebilehel.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Jaluse sisu</h3>
          <p className="text-sm text-gray-500 mt-1">
            Halda kõiki jaluses kuvatavaid tekste ja ettevõtte andmeid
          </p>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ettevõtte kirjeldus
            </label>
            <textarea
              value={footerContent.companyDescription}
              onChange={(e) => handleContentChange('companyDescription', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="Eesti metsandussektori juhtiv partner juba 20 aastat..."
            />
          </div>

          <div className="border-t pt-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">Ettevõtte andmed</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Käibemaksu staatus
                </label>
                <input
                  type="text"
                  value={footerContent.vatInfo}
                  onChange={(e) => handleContentChange('vatInfo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Käibemaksukohustuslane"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  KMKR number
                </label>
                <input
                  type="text"
                  value={footerContent.kmkrNumber}
                  onChange={(e) => handleContentChange('kmkrNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="KMKR nr EE123456789"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lahtiolekuajad
              </label>
              <input
                type="text"
                value={footerContent.businessHours}
                onChange={(e) => handleContentChange('businessHours', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Lahtiolekuajad: E-R 8:00-17:00"
              />
            </div>
          </div>

          <div className="border-t pt-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">Kontaktandmed</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aadress
                </label>
                <input
                  type="text"
                  value={footerContent.address}
                  onChange={(e) => handleContentChange('address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Osmussaare tänav 8, 13811 Tallinn, Eesti"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon 1
                  </label>
                  <input
                    type="text"
                    value={footerContent.phone1}
                    onChange={(e) => handleContentChange('phone1', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="+372 6707 110"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon 2
                  </label>
                  <input
                    type="text"
                    value={footerContent.phone2}
                    onChange={(e) => handleContentChange('phone2', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="+372 5663 6076"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-posti aadress
                </label>
                <input
                  type="email"
                  value={footerContent.email}
                  onChange={(e) => handleContentChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="info@forestryest.ee"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">Autoriõigus</h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Autoriõiguse tekst
              </label>
              <input
                type="text"
                value={footerContent.copyright}
                onChange={(e) => handleContentChange('copyright', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="© 2025 Forestry OÜ. Kõik õigused kaitstud. Reg.nr 12345678"
              />
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t">
            <button
              onClick={handleContentSave}
              disabled={saving}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvestamine...
                </>
              ) : (
                'Salvesta jaluse sisu'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterEditor;